import React, { useState } from 'react';
import FlipMove from "react-flip-move";
import CandidateAssessmentDetails from "../../../talent/CandidateAssessmentDetails";
import CircleProgress from "../../../../components/CircleProgress";
import { BreakdownPanel } from "../TalentProfileRoles";
import { AiDiamondIcon, EmptyPanel, ScoreBar, THEME_COLORS } from "./HireConfidenceParts";

/**
 * The expanded detail behind each Hire Confidence component.
 *
 * Each one answers "where did this number come from" for its own slice, and
 * nothing else — the section's structure only holds up if a breakdown can't be
 * mistaken for an explanation of the whole score.
 */

/* ─── Role Match ────────────────────────────────────────────── */

/**
 * One matched discipline's AI resume score, expandable to the full analysis.
 *
 * Reuses `BreakdownPanel` from the Roles section rather than restating it, so
 * the sub-scores, strengths and gaps stay identical wherever they appear.
 */
const DisciplineRow = ({ discipline, isOpen, onToggle }) => {
    const score = discipline.resume_match_score;
    const hasBreakdown = !!discipline.resume_match_breakdown;

    return (
        <div className={TB.mapClass('overflow-hidden rounded-lg border bg-white transition-colors duration-200', {
            'border-primary-lightest': isOpen,
            'border-[#E2E3EA]': !isOpen,
        })}>
            <button
                type="button"
                onClick={onToggle}
                disabled={!hasBreakdown}
                className="flex w-full flex-row items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-[#FAFAFB] disabled:cursor-default disabled:hover:bg-white"
            >
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-[#0E0E14]">
                        {discipline.name}
                    </span>
                    {/* Years of experience sits with the role it belongs to —
                        the same discipline can be a strong resume match and a
                        thin one on experience, and that gap is the finding. */}
                    <span className="block truncate text-xs text-[#595D68]">
                        {discipline.experience_label || 'Experience not set'}
                    </span>
                </span>

                {/* A labelled pill out of ten, matching the Roles section. A
                    filled circle here read as a peer of the AI Resume Score
                    ring above and competed with it — this is a supporting
                    figure, not a second headline. */}
                <span className="flex shrink-0 flex-row items-center gap-1.5 rounded-full border border-primary-lightest bg-[#FAF8FF] px-2.5 py-1">
                    <AiDiamondIcon className="h-3 w-3 shrink-0 text-primary" />
                    <span className="whitespace-nowrap text-xs font-medium text-primary-lighter">
                        Resume Score
                    </span>
                    <span className="whitespace-nowrap text-xs font-bold tabular-nums text-primary">
                        {(score / 10).toFixed(1)}
                        <span className="font-medium text-primary-lighter">/10</span>
                    </span>
                </span>

                <span className="flex shrink-0 flex-row items-center gap-2 text-xs font-semibold text-primary">
                    {hasBreakdown ? (
                        <>
                            {isOpen ? 'Hide' : 'Analysis'}
                            <i className={TB.mapClass('fas fa-chevron-down transition-transform duration-200', {
                                'rotate-180': isOpen,
                            })} />
                        </>
                    ) : (
                        <span className="text-[#A2A6AF]">No analysis</span>
                    )}
                </span>
            </button>

            {isOpen && hasBreakdown && (
                <div className="px-3 pb-3">
                    <BreakdownPanel discipline={discipline} />
                </div>
            )}
        </div>
    );
};

/**
 * The matched disciplines and their resume analyses, one open at a time.
 *
 * Shared by the Role Match breakdown and the standalone AI Resume Score
 * section, which shows the same list before a candidate has proposed. It owns
 * which row is open, so two mountings of it on one page don't fight over that.
 */
const ResumeDisciplineList = ({ disciplines, emptyChildren }) => {
    const [openDisciplineId, setOpenDisciplineId] = useState(null);

    if (!disciplines.length) {
        return (
            <EmptyPanel icon="fa-file-alt" title="No resume analysis yet">
                {emptyChildren}
            </EmptyPanel>
        );
    }

    return (
        <FlipMove
            duration={250}
            easing="ease-out"
            enterAnimation="fade"
            leaveAnimation="fade"
            className="flex flex-col gap-2"
        >
            {TB.map(disciplines, discipline => (
                <div key={discipline.id}>
                    <DisciplineRow
                        discipline={discipline}
                        isOpen={openDisciplineId === discipline.id}
                        onToggle={() => setOpenDisciplineId(
                            openDisciplineId === discipline.id ? null : discipline.id
                        )}
                    />
                </div>
            ))}
        </FlipMove>
    );
};

/**
 * One half of Role Match, with the share it carries and its own score.
 *
 * Stacked rather than columned: the resume half runs to several expandable
 * rows while the profile half is three bars, so side by side left one column
 * stranded against a wall of the other.
 */
const RoleMatchHalf = ({ title, icon, share, score, caption, color, isOpen, children }) => {
    const hasScore = score !== null && score !== undefined;

    return (
        <div>
            <div className="flex flex-row items-center gap-4">
                {/* Ringed rather than printed: these two are the halves the
                    whole component is built from, and a bare number beside a
                    heading read as an afterthought.

                    The value is driven from `isOpen`, so the fill animates up
                    from zero each time the accordion is opened rather than
                    only on first mount. */}
                <div className="h-[84px] w-[84px] shrink-0">
                    <CircleProgress
                        progress={isOpen && hasScore ? score : 0}
                        strokeWidth={9}
                        pathColor={color}
                        trailColor="#EFEFF3"
                    >
                        <p className="m-0">
                            <span className="text-xl">
                                {hasScore ? Math.round(score) : '—'}
                            </span>
                        </p>
                    </CircleProgress>
                </div>

                <div className="min-w-0">
                    {/* Bold and dark, one size below the component title above
                        it — the step in size and weight carries the hierarchy,
                        so it stays plainly readable rather than receding. */}
                    <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="inline-flex items-center gap-1.5 text-[15px] font-bold text-[#0E0E14]">
                            {icon}
                            {title}
                        </span>

                        <span className="text-xxs font-semibold uppercase tracking-[0.08em] text-[#A2A6AF]">
                            {share}% of Role Match
                        </span>
                    </div>

                    <p className="mb-0 mt-1 text-xs leading-[1.5] text-[#8B8F9A]">
                        {caption}
                    </p>
                </div>
            </div>

            <div className="mt-4">{children}</div>
        </div>
    );
};

/**
 * `criteriaHref` is the way back to the full criteria breakdown, which lives
 * further down the same profile page. Omitted where there is no such page to
 * point at — the modal on the candidate tile — rather than rendering a link
 * that scrolls nowhere.
 */
const RoleMatchBreakdown = ({ talent, metrics, isOpen, criteriaHref }) => {
    const halves = talent.roleMatchComponents?.() || [];
    const disciplines = talent.scoredDisciplines?.() || [];
    const profileHalf = TB.find(halves, ['key', 'profileMatch']);
    const resumeHalf = TB.find(halves, ['key', 'resumeMatch']);

    const skills = metrics?.skills_match;
    const experience = metrics?.experience_match;
    const industries = metrics?.industries_brands_match;

    return (
        <div className="flex flex-col gap-5">

            <RoleMatchHalf
                title="Profile Match"
                share={Math.round((profileHalf?.weight || 0) * 100)}
                score={profileHalf?.score}
                color={THEME_COLORS.warning}
                isOpen={isOpen}
                caption="Scored from what the talent has on their Wripple profile against the role’s criteria."
            >
                <div className="flex flex-col gap-3">
                    <ScoreBar
                        label="Skill Expertise"
                        score={skills?.percent}
                        isInactive={!skills?.skills_max_count}
                        detail={skills?.skills_max_count
                            ? `${skills.skills_count} of ${skills.skills_max_count} skills`
                            : 'No criteria set'}
                        colorClass="bg-[color:var(--theme-warning,#FFB946)]"
                    />
                    <ScoreBar
                        label="Role & Experience"
                        score={experience?.percent}
                        isInactive={!experience?.disciplines_max_count}
                        detail={experience?.disciplines_max_count
                            ? `${experience.disciplines_count} of ${experience.disciplines_max_count} roles`
                            : 'No criteria set'}
                        colorClass="bg-[color:var(--theme-warning,#FFB946)]"
                    />
                    <ScoreBar
                        label="Industry / Brand"
                        score={industries?.percent}
                        isInactive={!industries?.total_max_count}
                        detail={industries?.total_max_count
                            ? `${industries.total_count || 0} of ${industries.total_max_count} matched`
                            : 'No criteria set'}
                        colorClass="bg-[color:var(--theme-warning,#FFB946)]"
                    />
                </div>

                {/* The full pills, table and brand logos already live in the
                    Match Score section; linking beats reproducing them. */}
                {criteriaHref && (
                    <a
                        href={criteriaHref}
                        className="mt-3 inline-block text-xs font-semibold text-[#8A5A00] hover:underline"
                    >
                        See the full criteria breakdown ↑
                    </a>
                )}
            </RoleMatchHalf>

            <div className="border-t border-[#EDEEF2]" />

            <RoleMatchHalf
                title="AI Resume Score"
                icon={<AiDiamondIcon />}
                share={Math.round((resumeHalf?.weight || 0) * 100)}
                score={resumeHalf?.score}
                color={THEME_COLORS.primary}
                isOpen={isOpen}
                caption={disciplines.length > 1
                    ? `The AI’s read of the resume, scored against each of the ${disciplines.length} matched roles and averaged.`
                    : 'The AI’s read of the resume, scored against the matched role.'}
            >
                <ResumeDisciplineList
                    disciplines={disciplines}
                    emptyChildren={(
                        <>
                            None of this talent’s matched disciplines have been scored against
                            their resume, so Role Match is carried entirely by the profile half.
                        </>
                    )}
                />

            </RoleMatchHalf>
        </div>
    );
};

/* ─── Engagement Fit ────────────────────────────────────────── */

const EngagementFitBreakdown = ({ talent }) => {
    if (!talent.engagementFitAssessment?.()) {
        return (
            <EmptyPanel icon="fa-file-signature" title="No proposal assessed yet">
                Engagement Fit is scored from the AI’s review of a submitted proposal
                against this role’s criteria. It stays unearned until {talent.first_name || 'this talent'} proposes.
            </EmptyPanel>
        );
    }

    // Rendered bare: the accordion row already frames it and names the score,
    // so the modal's own card and inset would just be a box inside a box.
    return <CandidateAssessmentDetails candidate={talent} hideHeader flush />;
};

/**
 * Only two components have a panel. Market Demand and Prior Outcomes had one
 * too when the section was a four-row accordion, and it was a badge and three
 * stat tiles behind a click — the Hire Confidence section shows those on the
 * card face instead, so the panels went with the accordion.
 *
 * Mounted by name rather than exported as a single dispatch: each surface picks
 * the panel it needs, so the tile's Role Match modal doesn't have to pull in
 * the Engagement Fit one to get at it.
 */
export { RoleMatchBreakdown, EngagementFitBreakdown, ResumeDisciplineList };
